import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { FormContext } from 'ksnm-common-ui/lib/Form/FormWrapper/FormWrapperContext';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { Icon } from 'ksnm-common-ui/lib/Icon';

import { publicationRegisterStore } from '@stores/publicationRegisterStore';
import type { ReportingPackageDto } from '@src/api';

import styles from '../AddReportDrawer.module.scss';

export const Form = observer(() => {
  const [showInfo, setShowInfo] = useState(false);
  const { uncreatedReports, getUncreatedReports, publicationRegister } = publicationRegisterStore;

  const { submit } = useForm();
  const { isDirty, resetForm } = useContext(FormContext);

  const {
    input: { value: yearValue },
  } = useField('year');

  const {
    input: { value: groupValue },
  } = useField('group');

  const {
    input: { value: packageValue },
  } = useField('package');

  useEffect(() => {
    if (packageValue) {
      const periodCode = packageValue?.substring(packageValue?.lastIndexOf('P') + 1, packageValue?.lastIndexOf('V'));

      getUncreatedReports({
        reportPackageYear: yearValue,
        reportGroupCode: groupValue,
        reportPackageCode: packageValue,
        periodCode,
      }).then((data) => {
        if (data) setShowInfo(data?.length === 0);
      });
    }
  }, [getUncreatedReports, groupValue, packageValue, yearValue]);

  const yearItems = useMemo(() => {
    const output: string[] = [];
    publicationRegister.forEach((company) => company?.children?.forEach((year) => year.name && output.push(year.name)));

    return output.map((item) => ({ name: item, id: item }));
  }, [publicationRegister]);

  const groupItems = useMemo(() => {
    const output: string[] = [];
    publicationRegister.forEach(
      (company) =>
        company?.children?.forEach((year) => {
          if (yearValue && yearValue !== year.name) return;
          year?.children?.forEach((group) => group.name && output.push(group.name));
        }),
    );
    return [...new Set(output)].map((item) => ({ name: item, id: item.split(' - ')[0] }));
  }, [yearValue, publicationRegister]);

  const packageItems = useMemo(() => {
    const output: ReportingPackageDto[] = [];
    publicationRegister.forEach(
      (company) =>
        company?.children?.forEach(
          (year) =>
            year?.children?.forEach(
              (group) => group?.children?.forEach((packageItem) => packageItem && output.push(packageItem)),
            ),
        ),
    );

    return output
      .filter(
        (item) => `${item.reportPackageYear}` === yearValue && item.reportGroupCode === groupValue.split(' - ')[0],
      )
      .map((item) => ({ name: item.name, id: item.name }));
  }, [groupValue, publicationRegister, yearValue]);

  const onChangeYear = useCallback(
    (v) => {
      resetForm({ year: v });
    },
    [resetForm],
  );

  const onChangeGroup = useCallback(
    (v) => {
      resetForm({ year: yearValue, group: v });
    },
    [resetForm, yearValue],
  );

  const onChangePackage = useCallback(
    (v) => {
      resetForm({ year: yearValue, group: groupValue, package: v });
    },
    [groupValue, resetForm, yearValue],
  );

  return (
    <>
      <SelectField name="year" items={yearItems} label="Отчетный год" size="small" onChange={onChangeYear} />
      <SelectField
        name="group"
        items={groupItems}
        label="Код группы отчетности"
        size="small"
        onChange={onChangeGroup}
        disabled={!yearValue}
      />
      <SelectField
        name="package"
        items={packageItems}
        label="Код пакета отчетов"
        size="small"
        onChange={onChangePackage}
        disabled={!groupValue}
      />
      <SelectField
        name="report"
        items={uncreatedReports}
        label="Отчеты"
        mode="multiple"
        size="small"
        disabled={!packageValue}
      />
      {showInfo && packageValue && <p className={styles.info}>Для данного пакета нет подходящих версий отчета</p>}

      <Button className={styles.btn} icon={<Icon name="save" />} onClick={submit} disabled={!isDirty} size="large">
        Сохранить
      </Button>
    </>
  );
});
